import { FC } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { useTrainingBack } from "@/features/training/hooks/useTrainingBack";
import { formatDateTime } from "@/features/training/lib/trainingDateUtils";
import { TrainingSessionStatus } from "@/features/training/model/Training/TrainingSession";
import { CourseSessionSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import { CourseSessionActions } from "@/features/training/pages/training-tab/panel-right/training-session/CourseSessionActions";
import { CourseSessionStudents } from "@/features/training/pages/training-tab/panel-right/training-session/CourseSessionStudents";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import {
	Box,
	Chip,
	Divider,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import rehypeRaw from "rehype-raw";

import {
	useFetchCourse,
	useFetchCourseSession,
} from "@/service/TrainingQueries";

const humanize = (s?: string) =>
	s ? s.split(/(?=[A-Z])/).join(" ") : undefined;

const statusColor = (status?: string) => {
	switch (status) {
		case "Active":
			return "info";
		case "Complete":
		case "Completed":
			return "success";
		case "Archived":
			return "default";
		default:
			return "text.secondary";
	}
};

interface CourseSessionProps {
	courseId: number;
	sessionId: number;
}

export const CourseSession: FC<CourseSessionProps> = ({
	courseId,
	sessionId,
}) => {
	const theme = useTheme();
	const handleBack = useTrainingBack();
	const { timezone } = useTime();

	const { isLoading: isLoadingCourse, data: course } = useFetchCourse(courseId);
	const { isLoading: isLoadingSession, data: session } =
		useFetchCourseSession(sessionId);

	if (isLoadingSession || isLoadingCourse || !session || !course) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<CourseSessionSkeleton />
			</Box>
		);
	}

	const sessionDate = formatDateTime(session.SessionDate, timezone);
	const expiresDate = session.ExpiresDate
		? formatDateTime(session.ExpiresDate, timezone)
		: null;
	const statusPalette = statusColor(session.Status);

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header – aligned with DrillSession */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					flexWrap: "wrap",
					py: 1.15,
					px: { xs: 2, sm: 2.5 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tooltip
					title={<Translate String="Common.Back">Back</Translate>}
					enterDelay={400}
				>
					<IconButton
						onClick={handleBack}
						size="small"
						sx={{
							color: "primary.main",
							"&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
						}}
					>
						<ArrowBackIosIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>

				<Box sx={{ flex: 1, minWidth: 280 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.05,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.CourseSession.L135C7.Training">Training</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} noWrap title={sessionDate}>
						<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.CourseSession.L138C82.SessionDetails">Session details</Translate>
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 0.15 }}
						noWrap
						title={course.Name}
					>
						{sessionDate}
					</Typography>
					<Stack
						direction="row"
						spacing={0.75}
						useFlexGap
						flexWrap="wrap"
						sx={{
							mt: 0.5,
						}}
					>
						<Chip
							size="small"
							label={session.Status || "—"}
							sx={{
								height: 24,
								borderRadius: 2,
								fontWeight: 700,
								bgcolor:
									statusPalette === "info" || statusPalette === "success"
										? alpha(theme.palette[statusPalette].main, 0.1)
										: alpha(theme.palette.text.primary, 0.08),
								color:
									statusPalette === "info" || statusPalette === "success"
										? `${statusPalette}.main`
										: "text.secondary",
							}}
						/>
						<Chip
							size="small"
							label={course.Name}
							sx={{
								height: 24,
								maxWidth: "100%",
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								fontWeight: 700,
								"& .MuiChip-label": {
									display: "block",
									overflow: "hidden",
									textOverflow: "ellipsis",
								},
							}}
						/>
						{expiresDate && (
							<Chip
								size="small"
								label={`Expires ${expiresDate}`}
								sx={{
									height: 24,
									borderRadius: 2,
									bgcolor: alpha(theme.palette.warning.main, 0.1),
									fontWeight: 700,
								}}
							/>
						)}
					</Stack>
				</Box>

				<CourseSessionActions session={session} course={course} />
			</Box>

			{/* Overview card – full width */}
			<Box
				sx={{
					flexShrink: 0,
					px: { xs: 2, sm: 2.5 },
					py: 1.35,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						borderRadius: 3,
						borderColor: "divider",
					}}
				>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.1,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Training.Drill.Overview">Overview</Translate>
					</Typography>
					<Box
						sx={{
							mt: 1.5,
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
							gap: 2,
						}}
					>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Date">Date</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDateTime(session.SessionDate, timezone)}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Duration">
									Duration
								</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{humanize(session.Duration) || "—"}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Expires">Expires</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDateTime(session.ExpiresDate, timezone)}
							</Typography>
						</Box>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								display="block"
							>
								<Translate String="Training.Session.Trainer">Trainer</Translate>
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{session.Trainer?.Name || "—"}
							</Typography>
						</Box>
					</Box>
				</Paper>
			</Box>

			{/* Scrollable content */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable both-edges",
					overscrollBehavior: "contain",
					px: { xs: 2, sm: 2.5 },
					py: 1.75,
				}}
			>
				{/* Information + Comment */}
				{(session.Information || session.Comment) && (
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							mb: 2,
							borderRadius: 2,
							borderColor: "divider",
						}}
					>
						{session.Information && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8 }}
								>
									<Translate String="Training.Session.Info">Info</Translate>
								</Typography>
								<Box sx={{ mt: 1 }}>
									<SATOSContentRender
										isNotProcedureRender={true}
										rehypePlugins={[rehypeRaw]}
										content={session.Information}
									/>
								</Box>
							</Box>
						)}
						{session.Information && session.Comment && (
							<Divider sx={{ my: 2 }} />
						)}
						{session.Comment && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8 }}
								>
									<Translate String="Training.Session.Comment">
										Comment
									</Translate>
								</Typography>
								<Typography
									variant="body2"
									sx={{ mt: 1, whiteSpace: "pre-wrap" }}
								>
									{session.Comment}
								</Typography>
							</Box>
						)}
					</Paper>
				)}

				{/* Students */}
				{session.Students?.length ? (
					<CourseSessionStudents
						sessionId={session.Id}
						students={session.Students}
						areEditable={session.Status === TrainingSessionStatus.Active}
					/>
				) : (
					<Paper
						variant="outlined"
						sx={{
							p: 3,
							borderRadius: 2,
							textAlign: "center",
							borderColor: "divider",
							borderStyle: "dashed",
						}}
					>
						<PersonOutlinedIcon
							sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
						/>
						<Typography variant="body1" color="text.secondary" fontWeight={500}>
							<Translate String="Training.Session.NoParticipants">
								No participants yet.
							</Translate>
						</Typography>
						<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
							<Translate String="Training.Session.NoParticipants.Hint">
								Add participants to track attendance.
							</Translate>
						</Typography>
					</Paper>
				)}
			</Box>
		</Box>
	);
};
